import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { IsArchivedFilterOptions } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';

export type SortField =
  | 'updated_at'
  | 'num_likes'
  | 'num_downloads'
  | 'num_views'
  | 'relevance';

export type SortMode = {
  field: SortField;
  direction: 'asc' | 'desc';
};

const chips: (SortMode & {
  title: string;
})[] = [
  {
    field: 'num_likes',
    title: 'Top Favorited',
    direction: 'desc',
  },
  {
    field: 'num_views',
    title: 'Top Viewed',
    direction: 'desc',
  },
  {
    field: 'num_downloads',
    title: 'Top Downloaded',
    direction: 'desc',
  },
  {
    field: 'updated_at',
    title: 'Recently Updated',
    direction: 'desc',
  },
];

const chipsWithRelevance: typeof chips = [
  { field: 'relevance', title: 'Relevance', direction: 'desc' },
  ...chips,
];

const archiveModes: { mode: IsArchivedFilterOptions; title: string }[] = [
  { mode: 'false', title: 'Active' },
  { mode: 'true', title: 'Archived' },
  { mode: 'allow', title: 'All' },
];

function ArchiveDropdown({
  archiveMode,
  setArchiveMode,
}: {
  archiveMode: IsArchivedFilterOptions;
  setArchiveMode: (newMode: IsArchivedFilterOptions) => void;
}) {
  const index = archiveModes.findIndex((m) => m.mode === archiveMode);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="relative inline-flex h-7 cursor-pointer items-center justify-start whitespace-nowrap rounded border px-2 hover:bg-slate-100 hover:dark:bg-slate-800">
        <div className="inline-flex select-none gap-1 text-center text-sm font-medium leading-tight text-slate-900 dark:text-white">
          {archiveModes[index].title} <ChevronDownIcon className="h-4 w-4" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="">
        {archiveModes.map((mode, i) => (
          <DropdownMenuItem
            key={i}
            onClick={() => setArchiveMode(mode.mode)}
            className=""
          >
            {mode.title}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function SortChips({
  sortMode,
  setSortMode,
  showRelevance = false,
  archiveMode,
  setArchiveMode,
}: {
  sortMode: SortMode;
  setSortMode: (newMode: SortMode) => void;
  showRelevance?: boolean;
  archiveMode?: IsArchivedFilterOptions;
  setArchiveMode?: (newMode: IsArchivedFilterOptions) => void;
}) {
  const { value: useExactSearchForPrompts } = useOrgConfig(
    OrgConfigs.use_exact_search_for_prompts
  );

  const innerChips =
    showRelevance && !useExactSearchForPrompts ? chipsWithRelevance : chips;

  return (
    <div className="inline-flex h-7 w-full items-start justify-start gap-1">
      {innerChips.map((chip) => {
        const active = chip.field === sortMode.field;
        return (
          <div
            key={chip.field}
            className={cn(
              'inline-flex h-7 cursor-pointer items-center justify-start whitespace-nowrap rounded border px-2 hover:bg-slate-100 hover:dark:bg-slate-800',
              active
                ? 'border-transparent bg-slate-100 dark:bg-slate-800'
                : 'border-slate-200 dark:border-slate-700'
            )}
            onClick={() =>
              setSortMode({ field: chip.field, direction: chip.direction })
            }
          >
            <div className="inline-flex select-none gap-1 text-center text-sm font-medium leading-tight text-slate-900 dark:text-white">
              {chip.title}
            </div>
          </div>
        );
      })}
      {setArchiveMode && (
        <>
          <div className="grow" />
          <ArchiveDropdown
            archiveMode={archiveMode ?? 'false'}
            setArchiveMode={setArchiveMode}
          />
        </>
      )}
    </div>
  );
}
